import React, { lazy } from 'react';
import { RouteObject, Navigate } from 'react-router-dom';
import { RoutePermissionGuard } from '@shared/auth/RoutePermissionGuard';
import { Role } from '@shared/auth/AuthContext';

// Module-isolated page imports
const LiveClassroomPage = lazy(() => import('./modules/classroom/pages/LiveClassroomPage'));
const LiveSchedulePage = lazy(() => import('./modules/classroom/pages/LiveSchedulePage'));
const CoursesPage = lazy(() => import('./modules/courses/pages/CoursesPage'));
const CourseLearningPage = lazy(() => import('./modules/courses/pages/CourseLearningPage'));
const MaterialsPage = lazy(() => import('./modules/materials/pages/MaterialsPage'));
const AssessmentsPage = lazy(() => import('./modules/assessments/pages/AssessmentsPage'));
const ExamLivePage = lazy(() => import('./modules/assessments/pages/ExamLivePage'));
const ExamResultPage = lazy(() => import('./modules/assessments/pages/ExamResultPage'));
const QuizPage = lazy(() => import('./modules/assessments/pages/QuizPage'));
const QuestionsPage = lazy(() => import('./modules/questions/pages/QuestionsPage'));
const ReportsPage = lazy(() => import('./modules/assessments/pages/ReportsPage'));

// Assessment, Proctoring & Evaluation pages
const PdfExamCreatorPage = lazy(() => import('./modules/assessments/pages/PdfExamCreatorPage'));
const ProctoringDashboardPage = lazy(() => import('./modules/proctoring/pages/ProctoringDashboardPage'));
const ViolationAuditPage = lazy(() => import('./modules/proctoring/pages/ViolationAuditPage'));
const SecurityPoliciesPage = lazy(() => import('./modules/proctoring/pages/SecurityPoliciesPage'));
const CandidateVerifyGatePage = lazy(() => import('./modules/proctoring/pages/CandidateVerifyGatePage'));
const SubjectiveEvaluationPage = lazy(() => import('./modules/assessments/pages/SubjectiveEvaluationPage'));
const MockGeneratorPage = lazy(() => import('./modules/questions/pages/MockGeneratorPage'));

const FACULTY_ROLES: Role[] = ['owner', 'admin', 'branch_head', 'teacher'];

export const lmsRoutes: RouteObject[] = [
  {
    index: true,
    element: <Navigate to="live" replace />,
  },
  {
    path: 'live',
    handle: { crumb: 'Live Classroom' },
    element: <LiveClassroomPage />,
  },
  {
    path: 'schedule',
    handle: { crumb: 'Classes & Recordings' },
    element: <LiveSchedulePage />,
  },
  {
    path: 'courses',
    handle: { crumb: 'Curriculum & Courses' },
    element: (
      <RoutePermissionGuard permission="courses.manage" allowedRoles={FACULTY_ROLES}>
        <CoursesPage />
      </RoutePermissionGuard>
    ),
  },
  {
    path: 'learn',
    handle: { crumb: 'Course Player' },
    element: <CourseLearningPage />,
  },
  {
    path: 'materials',
    handle: { crumb: 'Learning Materials Vault' },
    element: <MaterialsPage />,
  },
  {
    path: 'assessments',
    handle: { crumb: 'Assessments & Tests' },
    element: (
      <RoutePermissionGuard permission="tests.author" allowedRoles={FACULTY_ROLES}>
        <AssessmentsPage />
      </RoutePermissionGuard>
    ),
  },
  {
    path: 'exam-portal',
    handle: { crumb: 'Live Exam Portal (CBT)' },
    element: <ExamLivePage />,
  },
  {
    path: 'exam-results',
    handle: { crumb: 'Exam Results & Solutions' },
    element: <ExamResultPage />,
  },
  {
    path: 'quiz',
    handle: { crumb: 'Speed Quiz Drill' },
    element: <QuizPage />,
  },
  {
    path: 'questions',
    handle: { crumb: 'Question Bank (Feeder)' },
    element: (
      <RoutePermissionGuard permission="tests.author" allowedRoles={FACULTY_ROLES}>
        <QuestionsPage />
      </RoutePermissionGuard>
    ),
  },
  {
    path: 'reports',
    handle: { crumb: 'Gradebook & Reports' },
    element: (
      <RoutePermissionGuard permission="analytics.read" allowedRoles={FACULTY_ROLES}>
        <ReportsPage />
      </RoutePermissionGuard>
    ),
  },
  {
    path: 'pdf-exams',
    handle: { crumb: 'PDF Exam Creator' },
    element: (
      <RoutePermissionGuard permission="tests.author" allowedRoles={FACULTY_ROLES}>
        <PdfExamCreatorPage />
      </RoutePermissionGuard>
    ),
  },
  {
    path: 'proctoring',
    handle: { crumb: 'Live Proctoring Room' },
    element: (
      <RoutePermissionGuard permission="proctoring.manage" allowedRoles={FACULTY_ROLES}>
        <ProctoringDashboardPage />
      </RoutePermissionGuard>
    ),
  },
  {
    path: 'violations',
    handle: { crumb: 'Violation Audit & Evidence' },
    element: (
      <RoutePermissionGuard permission="proctoring.manage" allowedRoles={FACULTY_ROLES}>
        <ViolationAuditPage />
      </RoutePermissionGuard>
    ),
  },
  {
    path: 'security-policies',
    handle: { crumb: 'Security & Lockdown Policies' },
    element: (
      <RoutePermissionGuard permission="proctoring.policy" allowedRoles={FACULTY_ROLES}>
        <SecurityPoliciesPage />
      </RoutePermissionGuard>
    ),
  },
  {
    path: 'candidate-verify',
    handle: { crumb: 'Candidate Verification Gate' },
    element: <CandidateVerifyGatePage />,
  },
  {
    path: 'evaluate',
    handle: { crumb: 'Subjective Exam Evaluation' },
    element: (
      <RoutePermissionGuard permission="tests.grade" allowedRoles={FACULTY_ROLES}>
        <SubjectiveEvaluationPage />
      </RoutePermissionGuard>
    ),
  },
  {
    path: 'mock-generator',
    handle: { crumb: 'AI Mock Test Generator' },
    element: (
      <RoutePermissionGuard permission="tests.author" allowedRoles={FACULTY_ROLES}>
        <MockGeneratorPage />
      </RoutePermissionGuard>
    ),
  },
];
